import type { Locale } from './locale';
import { formatTemperatureByLocale, formatUniversalUnitByLocale } from './units';

export type BioscopeValueKind = 'depth' | 'pitch' | 'roll' | 'waterTemperature';

const BIOSCOPE_UNITS: Record<BioscopeValueKind, string> = {
    depth: 'm',
    pitch: '°',
    roll: '°',
    waterTemperature: '°C',
};

/**
 * Parses raw bioscope value (string with unit or number) into numeric value and unit.
 * External service sends e.g. "32.6m", "1.7°", "9.0°C". Handles comma as decimal separator.
 */
export function parseBioscopeRaw(
    raw: string | number | undefined,
    kind: BioscopeValueKind
): { value: number; unit: string } | null {
    if (raw === undefined || raw === null) {
        return null;
    }
    const unit = BIOSCOPE_UNITS[kind];
    if (typeof raw === 'number') {
        if (Number.isNaN(raw)) return null;
        return { value: raw, unit };
    }
    const s = String(raw).trim();
    if (s === '') return null;
    // Strip known unit suffix (longest first for °C before °)
    const suffix = kind === 'waterTemperature' ? '°C' : kind === 'depth' ? 'm' : '°';
    const numPart = s.endsWith(suffix) ? s.slice(0, -suffix.length).trim() : s;
    const normalized = numPart.replace(',', '.');
    const value = Number.parseFloat(normalized);
    if (Number.isNaN(value)) return null;
    return { value, unit };
}

const DECIMALS: Record<BioscopeValueKind, number> = {
    depth: 1,
    pitch: 1,
    roll: 1,
    waterTemperature: 1,
};

/**
 * Formats a bioscope field value for display: strips unit from raw value, then formats
 * number by locale with the same unit (depth: m, pitch/roll: °, waterTemperature: °C).
 */
export function formatBioscopeValue(
    raw: string | number | undefined,
    kind: BioscopeValueKind,
    locale: Locale,
    fallback = '-'
): string {
    const parsed = parseBioscopeRaw(raw, kind);
    if (parsed === null) return fallback;
    const decimals = DECIMALS[kind];
    if (kind === 'waterTemperature') {
        return formatTemperatureByLocale(parsed.value, parsed.unit, locale, decimals, fallback);
    }
    return formatUniversalUnitByLocale(parsed.value, parsed.unit, locale, decimals, fallback);
}
